import { Component, inject, computed, effect } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { ScenarioStoreService } from "../../../services/store/scenario/scenario.store.service";
import { Observable } from "rxjs";
// Global, unencapsulated stylesheet: injected via this side-effect import, not Angular's
// styleUrls/styleUrl, so it isn't scoped to this component.
import "./scenario-chart.component.scss";

import { Scales } from "@bitwarden/web-vault/app/components/charts/shared/scales";
import { CalculatedBalancesArray } from "@bitwarden/web-vault/app/services/store/calculated-balance.collection.abstraction";
import { BaseBalanceChartComponent } from "@bitwarden/web-vault/app/components/charts/shared/base-balance-chart.component";
import { ScenarioPaths } from "@bitwarden/web-vault/app/models/enum/scenario.types";
import * as d3 from "d3";
import {
  InteractionState,
  InteractionStateKey,
} from "@bitwarden/web-vault/app/components/charts/shared/interaction-state";

// todo: @Horace rewrote some function here, need to look at base balance chart and refactor see how they can be reused

@Component({
  selector: "app-scenario-chart",
  templateUrl: "./scenario-chart.component.html",
})
export class ScenarioChartComponent extends BaseBalanceChartComponent {
  // Scenario graph specific properties
  protected graphContainerSelectString = "div#scenarioLineChart";
  protected graphContainerID = "scenarioGraphContainer";
  protected graphGroupID = "scenarioLineChartGroup";

  // subscribe to signal stores
  private scenarioStoreService = inject(ScenarioStoreService);
  protected groupControl = this.scenarioStoreService.groupings.selected();
  private balanceByTime$: Observable<CalculatedBalancesArray> = toObservable(
    this.scenarioStoreService.balanceByTime.balances,
  );

  private lineColours = ["#496A71", "#0081B8", "#2A6CE2"];

  // Graph Data
  private balancesByTime: Map<string, number>[];
  private insByTime: Map<string, number>[];
  private outsByTime: Map<string, number>[];
  private allBalancePointData: number[][][] = [];
  private allBalancePointDates: number[][] = []; // Store dates for each path

  private selectedPathIndex = computed(() => {
    const selectedPath = this.scenarioStoreService.currentScenarioType();
    return ScenarioPaths?.[selectedPath] ?? 0;
  });

  // New properties to track pinned path and index
  private pinnedPathIndex: number = null;
  pinnedIndex: number = null;

  constructor() {
    super();
    this.balanceByTime$.pipe(takeUntilDestroyed()).subscribe(this.updateGraph.bind(this));

    effect(() => {
      const pathIndex = this.selectedPathIndex();
      if (this.graphElementsInitialized) {
        // A pin belongs to the path it was made on: keep it while that path is the selected one
        // (mousedown selects the clicked path before drawing the pin, and this runs after it),
        // drop it when another path gets selected from outside the chart.
        if (this.pinnedIndex !== null && this.pinnedPathIndex === pathIndex) {
          this.updatePathVisual(InteractionState.Pinned, pathIndex, this.pinnedIndex);
        } else {
          this.pinnedPathIndex = null;
          this.pinnedIndex = null;
          this.updatePathVisual(InteractionState.Default, pathIndex);
        }
      }
    });
  }

  /**
   * Triggers when the balance numbers have updated on the store
   *
   */
  updateGraph() {
    super.updateGraph();
    // get the granularity from the groupings signal
    this.updateGranularity();

    // Create a local copy of the balance by time Map
    const balancePoints = this.scenarioStoreService.balanceByTime.balancePoints();

    if (!balancePoints || balancePoints.length === 0) {
      // #4602: this is the actual reachable null path (the length-0 check further below can
      // never trigger - this.balancesByTime is populated from this same balancePoints right
      // before it, so it's never empty by the time that check runs). Previously removed the
      // marker line and the in/out boxes but not the balance lines or point circles, so those
      // two were left stale on screen. Mirrors
      // BalanceChartComponent's null-path cleanup (see #4157): this class draws its own boxes/
      // marker via updatePathVisual/updateInOutBoxes below, so nothing else clears them once
      // balancePoints goes empty.
      this.dataLine?.removeMarkerLine();
      this.dataLine?.removeBalanceLines();
      this.dataPoints?.removeAllPoints();
      this.clearPinnedBoxes();
      // Reset both point arrays so a pointer already over the chart doesn't redraw stale boxes/
      // markers from the previous render. This class overrides handleMouseMoveEvent/
      // handleMouseDownEvent/addMouseLeaveEvent, so the base class's balancePointData guards on
      // those methods never run here - the guard in updatePathVisual below (the single funnel all
      // of them route through) is what actually protects this class.
      this.balancePointData = [];
      this.allBalancePointData = [];
      return;
    }

    this.balancesByTime = [];
    this.insByTime = [];
    this.outsByTime = [];
    const openingPoints: number[][] = [];

    balancePoints.forEach((balancePointsPath) => {
      const balancesByTime = new Map<string, number>();
      const insByTime = new Map<string, number>();
      const outsByTime = new Map<string, number>();
      const opening: number[] = [];
      let index = 0;

      balancePointsPath.forEach((balancePoint) => {
        balancesByTime.set(balancePoint.plotDate.toString(), balancePoint.balance);
        insByTime.set(balancePoint.plotDate.toString(), balancePoint.ins);
        outsByTime.set(balancePoint.plotDate.toString(), balancePoint.outs);

        if (balancePoint.plotType === "opening") {
          opening.push(index);
        }
        index++;
      });

      this.balancesByTime.push(balancesByTime);
      this.insByTime.push(insByTime);
      this.outsByTime.push(outsByTime);
      openingPoints.push(opening);
    });

    const startDate = this.scenarioStoreService.period?.selected()?.startDate;
    const endDate = this.scenarioStoreService.period?.selected()?.endDate;

    const tickValues = [...this.balancesByTime[0].keys()].map(
      (time) => new Date(Number(time) * 1000),
    );

    if (!this.graphElementsInitialized) {
      this.initGraphElements();
    }

    if (this.dataLine) {
      this.dataLine.removeMarkerLine();
    }

    this.drawScalesFromData(
      startDate,
      endDate,
      this.balancesByTime,
      this.insByTime,
      this.outsByTime,
      tickValues,
    );
    this.drawLines(this.balancesByTime);
    this.drawPoints(this.balancesByTime, openingPoints);
    this.updatePathVisual(InteractionState.Default, this.selectedPathIndex());
  }

  // <editor-fold desc="Graph Drawing Functions">
  private drawScalesFromData(
    startDate: Date,
    endDate: Date,
    balancesByTime: Map<string, number>[],
    insByTime: Map<string, number>[],
    outsByTime: Map<string, number>[],
    tickValues?: Date[],
  ) {
    const [minYAxisValue, maxYAxisValue] = Scales.calculateMaxMinYAxisValuesList(
      balancesByTime,
      insByTime,
      outsByTime,
    );
    this.drawScales(startDate, endDate, minYAxisValue, maxYAxisValue, tickValues);
  }

  private getClosestPathAndPointIndex(e: MouseEvent): { pathIndex: number; pointIndex: number } {
    const pointerCoords = d3.pointer(e);
    const mouseX = pointerCoords[0] - this.leftMargin;
    const mouseY = pointerCoords[1];
    let minDistance = Infinity;
    let closest = { pathIndex: 0, pointIndex: 0 };

    this.allBalancePointData.forEach((pathPoints, pathIdx) => {
      pathPoints.forEach((point, pointIdx) => {
        const dx = point[0] - mouseX;
        const dy = point[1] - mouseY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance < minDistance) {
          minDistance = distance;
          closest = { pathIndex: pathIdx, pointIndex: pointIdx };
        }
      });
    });
    return closest;
  }

  private drawPoints(balancesByTime: Map<string, number>[], openingPoints: number[][]) {
    this.allBalancePointData = [];
    this.allBalancePointDates = [];
    balancesByTime.forEach((balanceSet, pathIndex) => {
      const balancePoints = this.dataPoints.generatePointData(balanceSet);
      const balanceDates = this.dataPoints.generatePointDates(balanceSet);
      this.allBalancePointData.push(balancePoints);
      this.allBalancePointDates.push(balanceDates);
      this.balancePointData = balancePoints;
      this.balancePointDates = balanceDates;
      this.dataPoints.updatePoints(
        `balancePoints_${pathIndex}`,
        balancePoints,
        `balance-points path-${pathIndex}`,
      );
      this.dataPoints.setOpeningPoints(`balancePoints_${pathIndex}`, openingPoints?.[pathIndex]);

      balancePoints.forEach((_, pointIndex) => {
        this.balanceGraph
          .selectAll(`#balancePoints_${pathIndex}_${pointIndex}`)
          .style("cursor", "pointer")
          .on("click", () => {
            // Pinning (and un-pinning) is handleMouseDownEvent's job, which already ran for this
            // click: redoing it here would put back a pin that mousedown just toggled off.
            this.scenarioStoreService.currentScenarioType.set(
              Object.keys(ScenarioPaths)[pathIndex],
            );
          });
      });
    });
  }

  private drawLines(balancesByTime: Map<string, number>[]) {
    balancesByTime.forEach((balanceSet, pathIndex) => {
      this.dataLine.updateBalanceLineGraph(
        balanceSet,
        `balanceLine_${pathIndex}`,
        `balance-line path-${pathIndex}`,
        this.lineColours[pathIndex],
      );
      this.balanceGraph
        .selectAll(`#balanceLine_${pathIndex}`)
        .style("cursor", "pointer")
        .on("click", () => {
          this.scenarioStoreService.currentScenarioType.set(Object.keys(ScenarioPaths)[pathIndex]);
        });
    });
  }

  protected handleMouseMoveEvent(e: MouseEvent): any {
    const { pathIndex, pointIndex } = this.getClosestPathAndPointIndex(e);
    this.hoveredIndex = pointIndex;
    this.updatePathVisual(InteractionState.Hover, pathIndex, pointIndex);
  }

  protected handleMouseDownEvent(e: MouseEvent): any {
    // Unlike handleMouseMoveEvent, this handler mutates persistent state (currentScenarioType,
    // pinnedPathIndex/pinnedIndex) before ever calling updatePathVisual, so updatePathVisual's own
    // guard runs too late to help: on an empty chart, getClosestPathAndPointIndex falls back to its
    // seeded {pathIndex: 0, pointIndex: 0} default and this method would otherwise reassign the
    // user's current scenario selection and arm a bogus pinned index for whenever data returns.
    if (!this.allBalancePointData.length) {
      return;
    }
    const { pathIndex, pointIndex } = this.getClosestPathAndPointIndex(e);
    if (this.selectedPathIndex() !== pathIndex) {
      this.scenarioStoreService.currentScenarioType.set(Object.keys(ScenarioPaths)[pathIndex]);
    }
    if (this.pinnedPathIndex === pathIndex && this.pinnedIndex === pointIndex) {
      this.pinnedPathIndex = null;
      this.pinnedIndex = null;
    } else {
      this.pinnedPathIndex = pathIndex;
      this.pinnedIndex = pointIndex;
    }
    if (this.pinnedIndex !== null && this.pinnedPathIndex !== null) {
      this.updatePathVisual(InteractionState.Pinned, this.pinnedPathIndex, this.pinnedIndex);
    } else {
      this.updatePathVisual(InteractionState.Default, pathIndex);
    }
  }

  private updatePathVisual(action: InteractionStateKey, pathIndex: number, index?: number) {
    // handleMouseMoveEvent/handleMouseDownEvent/addMouseLeaveEvent (all overridden below) and the
    // selectedPathIndex effect (constructor) all route through here, so guarding this single
    // funnel covers all four call sites instead of guarding each separately. Without it, once
    // the chart is truly empty (see updateGraph's null path above) the "else" branch below
    // computes lastIndex = -1, and setDefaultPoint(-1) destructures undefined and throws. The
    // legitimate call from updateGraph (line ~146) is unaffected: drawPoints already repopulates
    // allBalancePointData/balancePointData before that call runs.
    //
    // Guard on allBalancePointData, not balancePointData: drawPoints' loop overwrites
    // balancePointData on every iteration, so after the loop it only reflects the *last*
    // processed scenario path, while allBalancePointData accumulates all of them. Guarding on
    // balancePointData meant a single empty path (e.g. the last-processed one having zero points)
    // silently disabled hover/click/scenario-switch for the entire chart, even with the other
    // paths fully populated.
    if (!this.allBalancePointData.length) {
      return;
    }
    this.balanceGraph.selectAll(".balance-line").classed("selected-path", false);
    this.balanceGraph.selectAll(".balance-points").classed("selected-path", false);
    if (pathIndex !== undefined) {
      this.balanceGraph.selectAll(".path-" + pathIndex).classed("selected-path", true);
    }
    if (
      (action === InteractionState.Hover || action === InteractionState.Pinned) &&
      index !== undefined
    ) {
      this.setMarkerPinnedScenario(index, pathIndex);
      this.setHudData(index);
      this.selectPointsScenario(index, pathIndex);
      this.updateInOutBoxes(index);
      this.axis.highlightXAxisTick(this.allBalancePointDates[pathIndex][index]);
    } else {
      // Drop the hovered point's ring: nothing else clears it once the pointer leaves or the path
      // switches (this class doesn't override selectPoint, so the base class's version is a no-op).
      this.dataPoints.clearSelectedPoint();
      // The Default branch was written against balancePointData back when that was the only
      // field; drawPoints' loop overwrites it on every iteration, so it only ever reflects the
      // *last* processed path. Index off allBalancePointData[pathIndex] instead so the "last
      // point" is the last point of the path actually being defaulted, not whichever path
      // drawPoints happened to finish on - and so a path with zero points doesn't send
      // setDefaultPoint(-1) into a destructure of undefined while other paths are populated.
      const points = this.allBalancePointData[pathIndex] ?? [];
      if (!points.length) {
        // Nothing to default to: hide the previous path's marker line (opacity 0, like initMarker)
        // rather than leave it at that path's coordinates. Hide, not remove, so hover/pin's
        // moveMarkerLine can still show it (#4976).
        this.dataLine.createMarkerLine(0);
        this.updateInOutBoxes();
        this.axis.resetXAxisHighlight();
        return;
      }
      const lastIndex = points.length - 1;
      this.setDefaultPoint(lastIndex, pathIndex);
      this.updateInOutBoxes();
      this.axis.resetXAxisHighlight();
    }
  }

  protected setMarkerPinned() {
    this.setMarkerPinnedScenario(this.pinnedIndex, this.selectedPathIndex());
  }

  private setMarkerPinnedScenario(index: number, pathIndex: number) {
    const [closestPointX, closestPointY] = this.getPathCoordinatesScenario(index, pathIndex);
    this.dataLine.moveMarkerLine(closestPointX, closestPointY);
    this.dataLine.setMarkerLinePinned(true, false, `path-${pathIndex}`);
  }

  protected getPathCoordinatesScenario(dataPointIndex: number, pathIndex: number): number[] {
    return this.allBalancePointData?.[pathIndex]?.[dataPointIndex];
  }

  selectPointsScenario(i: number, pathIndex: number) {
    this.dataPoints.setSelectedPoint(`balancePoints_${pathIndex}`, i);
  }

  updateInOutBoxes(index?: number) {
    this.drawInsAndOuts(
      this.balancesByTime[this.selectedPathIndex()],
      this.insByTime[this.selectedPathIndex()],
      this.outsByTime[this.selectedPathIndex()],
      index,
    );
  }

  private setDefaultPoint(lastIndex: number, pathIndex: number) {
    // Coordinates come from the path actually being defaulted, not from balancePointData, which
    // drawPoints' loop overwrites on every iteration so it only reflects the last path drawn.
    const [lastPointX, lastPointY] = this.getPathCoordinatesScenario(lastIndex, pathIndex);
    this.setHudData(lastIndex);
    this.dataLine.createMarkerLine(0, lastPointX, lastPointY, 1);
    this.dataLine.setMarkerLinePinned(false, true, `path-${pathIndex}`);
    this.dataPoints.setDefaultPoint(`balancePoints_${pathIndex}`, lastIndex);
  }

  protected addMouseLeaveEvent() {
    this.graphContainer.on("mouseleave", () => {
      if (this.pinnedIndex !== null && this.pinnedPathIndex !== null) {
        this.updatePathVisual(InteractionState.Pinned, this.pinnedPathIndex, this.pinnedIndex);
      } else {
        this.updatePathVisual(InteractionState.Default, this.selectedPathIndex());
      }
    });
  }

  // </editor-fold desc="Graph Drawing Functions">
}
